import { useState } from "react";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";

import { ENV_VARS } from "@/constants/env";
import { getAuthTokensFromLocalStorage } from "@/utils/auth";
import { languageHeader } from "@/i18n/request-language";

const extractFilename = (header: string | null, fallback: string): string => {
  if (!header) return fallback;
  const match = header.match(/filename\*?=(?:UTF-8'')?["']?([^"';]+)["']?/i);
  return match?.[1] ? decodeURIComponent(match[1]) : fallback;
};

const triggerBlobDownload = (blob: Blob, fileName: string) => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
};

export const useDownloadFinalSettlement = () => {
  // Messages go through i18n: they showed in English on Arabic screens (B10).
  const { t } = useTranslation("company");
  const [isDownloading, setIsDownloading] = useState(false);

  const fetchBlob = async (id: number, fallback: string) => {
    const tokens = getAuthTokensFromLocalStorage();
    if (!tokens?.accessToken)
      throw new Error(t("payroll_downloads.not_authenticated"));
    const baseUrl = ENV_VARS.API_INSTANCE.replace(/\/$/, "");
    const res = await fetch(
      `${baseUrl}/company/payroll/final-settlements/${id}/download`,
      {
        method: "GET",
        headers: {
          Authorization: `Bearer ${tokens.accessToken}`,
          ...languageHeader(),
        },
      },
    );
    if (!res.ok) {
      let message = t("payroll_downloads.failed_settlement_status", {
        status: res.status,
      });
      try {
        const data = await res.json();
        if (data?.message) message = data.message;
      } catch {
        // not json
      }
      throw new Error(message);
    }
    const blob = await res.blob();
    const name = extractFilename(
      res.headers.get("content-disposition"),
      fallback,
    );
    return { blob, name };
  };

  const download = (id: number, { fileName }: { fileName?: string } = {}) => {
    const fallback = fileName ?? `final-settlement-${id}.pdf`;
    setIsDownloading(true);
    return toast
      .promise(
        fetchBlob(id, fallback).then(({ blob, name }) => {
          triggerBlobDownload(blob, name);
        }),
        {
          loading: t("payroll_downloads.preparing_settlement"),
          success: t("payroll_downloads.settlement_downloaded"),
          error: (err) =>
            err?.message ?? t("payroll_downloads.failed_settlement"),
        },
      )
      .finally(() => setIsDownloading(false));
  };

  return { download, isDownloading };
};
